import { useData } from "../context/DataContext";
import {
  Users,
  GraduationCap,
  ClipboardList,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { cn } from "../utils/cn";
import { calculateDataSummary, getStatusConfig } from "../utils/helpers";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Dashboard = () => {
  const { data } = useData();

  const summary = calculateDataSummary(data);
  const totalLektionen = data.zuteilungen.reduce(
    (sum, z) => sum + z.lektionen,
    0
  );

  const sortedLehrpersonen = [...summary.lehrpersonenStats].sort((a, b) => {
    return a.stats.differenz - b.stats.differenz;
  });

  // Calculate workload statistics
  const workloadStats = {
    unterbelastet: summary.unterbelastet,
    ausgeglichen: summary.ausgeglichen,
    ueberbelastet: summary.ueberbelastet,
  };

  // Prepare chart data
  const workloadChartData = sortedLehrpersonen.slice(0, 10).map((lp) => {
    return {
      name: lp.name.split(" ").slice(-1)[0], // Last name only
      Soll: lp.stats.soll,
      Ist: lp.stats.ist,
      differenz: lp.stats.differenz,
    };
  });

  const statusPieData = [
    {
      name: "Unterbelastet",
      value: workloadStats.unterbelastet,
      color: "#f59e0b",
    },
    {
      name: "Ausgeglichen",
      value: workloadStats.ausgeglichen,
      color: "#10b981",
    },
    {
      name: "Überbelastet",
      value: workloadStats.ueberbelastet,
      color: "#ef4444",
    },
  ];

  const statCards = [
    {
      label: "Lehrpersonen Total",
      value: data.lehrpersonen.length,
      icon: Users,
      color: "blue",
      trend: null,
    },
    {
      label: "Klassen Total",
      value: data.klassen.length,
      icon: GraduationCap,
      color: "green",
      trend: null,
    },
    {
      label: "Zuteilungen",
      value: data.zuteilungen.length,
      icon: ClipboardList,
      color: "purple",
      trend: null,
    },
    {
      label: "Total Lektionen",
      value: totalLektionen.toFixed(1),
      icon: Clock,
      color: "orange",
      trend: null,
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          const colorClasses = {
            blue: "bg-blue-50 text-blue-600",
            green: "bg-green-50 text-green-600",
            purple: "bg-purple-50 text-purple-600",
            orange: "bg-orange-50 text-orange-600",
          }[card.color];

          return (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600">
                    {card.label}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {card.value}
                  </p>
                </div>
                <div className={cn("p-3 rounded-lg", colorClasses)}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Workload Distribution */}
      {data.lehrpersonen.length > 0 && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Unterbelastet
                </h3>
              </div>
              <p className="text-4xl font-bold text-amber-600">
                {workloadStats.unterbelastet}
              </p>
              <p className="text-sm text-gray-500 mt-1">Lehrpersonen</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Ausgeglichen
                </h3>
              </div>
              <p className="text-4xl font-bold text-green-600">
                {workloadStats.ausgeglichen}
              </p>
              <p className="text-sm text-gray-500 mt-1">Lehrpersonen</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Überbelastet
                </h3>
              </div>
              <p className="text-4xl font-bold text-red-600">
                {workloadStats.ueberbelastet}
              </p>
              <p className="text-sm text-gray-500 mt-1">Lehrpersonen</p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Workload Comparison Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Stunden: Soll vs. Ist (Top 10)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={workloadChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Soll" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Ist" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Status Distribution Pie Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Workload Verteilung
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusPieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      percent ? `${name}: ${(percent * 100).toFixed(0)}%` : name
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {/* Teacher Overview Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Lehrer-Übersicht (Workload)
          </h2>
        </div>
        {data.lehrpersonen.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-gray-500">
              Keine Daten vorhanden. Klicke auf "Demo-Daten laden" um zu
              starten.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fächer
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vertr. Lekt.
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eff. Soll
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ist Std
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entschäd.
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Differenz
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedLehrpersonen.map((lp) => {
                  const stats = lp.stats;
                  const statusConfig = getStatusConfig(stats.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <tr
                      key={lp.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {lp.name}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {lp.faecher.join(", ")}
                        </div>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {lp.solllektionen}
                        {stats.hatAltersentlastung && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title="Altersentlastung">
                            AE
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {stats.soll.toFixed(1)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {stats.ist.toFixed(1)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                        {stats.entschaedigung.toFixed(1)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                        <span
                          className={cn(
                            "font-semibold",
                            stats.differenz > 0
                              ? "text-red-600"
                              : stats.differenz < 0
                              ? "text-amber-600"
                              : "text-green-600"
                          )}
                        >
                          {stats.differenz > 0 ? "+" : ""}
                          {stats.differenz.toFixed(1)}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <span
                          className={cn(
                            "inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium",
                            statusConfig.bg,
                            statusConfig.text
                          )}
                        >
                          <StatusIcon className="w-3.5 h-3.5" />
                          <span>{stats.status}</span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
